import * as actions_admin from 'actions/admin-actions';
import * as actions_service_requests from 'actions/service-requests-actions';
import * as actions_work_orders from 'actions/work-orders-actions';
import * as toolbox from 'components/common/toolbox';
import ActivityLog from 'components/activity-log/activity-log';
import Authorize from 'components/common/authorize';
import EllipsisDropdown from 'components/common/ellipsis-dropdown';
import ExpandDetails from 'components/common/expand-details';
import React, { useEffect, useRef, useState } from 'react';
import SpeedDialButton from 'components/common/speed-dial-button'; 
import StatusSelect from 'components/common/status-select';
import _ from 'lodash';
import moment from 'moment';
import { DashboardDetail, Spinner, ModalForm } from 'enspire-manager-framework';
import { APPS, INVOICES,LOG, PERMISSIONS, REQUESTS, SERVICE_REQUEST_TERMS, WORKORDERS } from 'components/constants';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

const ServiceRequestDashboard = (props) => {
    
    /* Hooks ------------------------------------------------------------------------------------------------------------------------------------*/
    
    const seg = 5;
    const params = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const expandRef = useRef();    
    const routeLocation = useLocation();
    
	const profiles = useSelector((state) => state.profiles);
	const user = useSelector(store => store.users?.user);
	const user_permissions = useSelector(store => store.users?.user_permissions);
    const admin = useSelector((store) => store.admin);
    const assets = useSelector((store) => store.assets.assets);
    const employees = useSelector((store) => store.employees.employees);
    const features = useSelector((store) => store.settings?.settings?.features)?.[(params.appId == APPS.DISPATCH.id) ? 'dispatch' : 'assets'];
    const service_request = useSelector((store) => store.serviceRequests.service_request);
    const services = useSelector((store) => store.serviceRequests);
    const workOrders = useSelector((store) => store.workOrders);

    const [state, setState] = useState({
        mapAddress: null,
        showGenerateInvoice: false,
    });
    const [expanded, setExpanded] = useState(0);
    const [showActivityModal, setShowActivityModal] = useState(false);
    
	const profile = profiles.profile;
    const pathname = routeLocation.pathname;
    const segments = pathname.split('/').length;
    const asset = assets.find((asset) => asset.id == service_request?.assetId);
    const isRecord = (service_request?.type == 'asset_record');
    const owner = _.find(employees, (o) => o.contact?.email == service_request?.ownerId);
    const createdBy = (owner) ? `${owner.contact?.firstName} ${owner.contact?.lastName}` : service_request?.ownerId;
    const isServiceCall = (service_request?.serviceRequestTermsId == SERVICE_REQUEST_TERMS.SERVICE_CALL.id);
    const segmentsClosed = (segments >= 12 && !isServiceCall);

    // Permissions ----------------------------------------

    const canInvoice = !!workOrders?.work_orders?.find((workOrder) => (workOrder.statusId == WORKORDERS.COMPLETE.id));
    const canReceivePayment = !!services?.service_request_invoices?.find((invoice) => (invoice.statusId != INVOICES.PAID.id && invoice.profileId));
    const canViewMySR = 
        (service_request?.techIds?.includes(user?.profileId) || service_request?.techIds?.includes(user?.email));
    const canEditSR = (
        user_permissions[PERMISSIONS.SERVICE_REQUESTS_EDIT.key] || 
        (canViewMySR && user_permissions[PERMISSIONS.SERVICE_REQUESTS_ASSIGNED_EDIT.key])
    );

    /* Effects ---------------------------------------------------------------------------------------------------------------------------*/

    useEffect(() => {
        dispatch(actions_service_requests.subServiceRequest(params.handle, params.request_id));
        dispatch(actions_work_orders.subWorkOrdersByRequestId(params.handle, params.request_id));

        return () => {
            var unsubscribe = services.service_request_unsubscribe;
            if (typeof unsubscribe === 'function') unsubscribe();

            var unsubscribe = workOrders.work_orders_unsubscribe;
            if (typeof unsubscribe === 'function') unsubscribe();

            dispatch(actions_service_requests.clearServiceRequest());
        };
    }, [params.request_id]);

    // Once customer is loaded, get the address
    useEffect(() => {
        let address = _.find(profiles?.profile?.addresses, { id: service_request?.addressId });
        if (!address) address = profiles?.profile?.addresses?.[0];
        let mapAddress = (address) 
            ? toolbox.format_address(address.addressLine1, address.addressLine2, address.city, address.state, address.zip)
            : null;
        if (mapAddress) setState(prev => ({ ...prev, mapAddress }));
    }, [profiles?.profile?.id]);

    // Trigger lightbox when URL param changes ------------------------------------------------------------------
    useEffect(() => {
        if (params.form == 'lightbox-sr') {
            dispatch(actions_admin.lightboxSources([{ src: service_request.imageUrl }]));
            dispatch(actions_admin.lightboxIndex(0));
            dispatch(actions_admin.lightboxShow(true));
        } else if (params.form == 'form'){
            dispatch(actions_admin.lightboxShow(false));
        }
    }, [params.form]);

    useEffect(() => {
        handleShowDetails(!segmentsClosed);
    }, [segmentsClosed])
    
    /* Actions/Handlers ------------------------------------------------------------------------------------------------------------------------------------*/

    const handleSpeedDial = (value, action) => {
        if (value == 'invoice') handleGenerateInvoice();
        if (value == 'payment') openReceivePayment();
        if (value == 'quote') newQuote();
        if (value == 'work-order') newWorkOrder();
    }
    const handleShowDetails = (value) => {
        (value) ? expandRef.current?.setOpen() : expandRef.current?.setClosed();
    }
    const openForm = () => {
        let location = toolbox.modifyPath(routeLocation.pathname, seg, 'request-form/' + params.request_id);
        history.push({ pathname: location });
    };
    const newQuote = () => {
        let location = toolbox.modifyPath(routeLocation.pathname, 5, `quote-form/0`);
        history.push({ pathname: location });
    };
    const newWorkOrder = () => {
        let location = toolbox.modifyPath(routeLocation.pathname, seg, 'work-order-form/0');
        history.push({ pathname: location });
    }
    const handleGenerateInvoice = () => {
        let newLocation = toolbox.modifyPath(routeLocation.pathname, seg, 'invoice-form/0');
        history.push({ pathname: newLocation });
    };
    const openReceivePayment = () => {
        let newLocation = toolbox.modifyPath(routeLocation.pathname, seg, 'payment-form/0');
        history.push({ pathname: newLocation });
    };

    const openHelpPanel = (url) => {
        if (window.flutter) {
            window.flutter.callHandler('helpPanel', url); 
        } else {
            dispatch(actions_admin.showHelp(true, url));
        }
	}
	const toggleLightbox = () => {
        let location = toolbox.modifyPath(pathname, seg, "lightbox-sr/0");
        history.push({ pathname: location });
	}

    /* Constants ------------------------------------------------------------------------------------------------------------------------------------*/

    var contactArray = []; // start will all contacts, remove when assigned an address
    if (profile?.contacts) profile.contacts.forEach((contact) => {
        contactArray.push(contact);
    });
    
    /* Render ------------------------------------------------------------------------------------------------------------------------------------*/

    return (

		<div className="row position-relative top-tab">
            {service_request
                ?   <Authorize perm={ PERMISSIONS.SERVICE_REQUESTS_VIEW } or={ canViewMySR } show403>

                        <div id="request-avatar" className={ 'col-12 mb-3' }>
                            <div className="pr-2">
                                <div className="d-flex justify-content-between">
                                    <h1 className="main-heading heading-service-request align-self-start mx-2">
                                        Service Id #{service_request.customId}
                                    </h1>
                                    <p className="mb-2 mr-auto">{ moment(service_request.requestDate?.toDate()).format('MMM Do, YYYY') }</p>
                                    { service_request.imageUrl && 
                                        <i className="fa-solid fa-circle-camera text-muted fa-3x mt-n2 position-absolute" style={{ right: '70px' }} role="button" onClick={ toggleLightbox }></i>
                                    }
                                    {(segments < seg+7 || (segments < seg+8 &&isServiceCall)) &&
                                        <div className={ 'animated fadeIn mt-n2 ml-3' } style={{ cursor: 'pointer', height: '10px' }} onClick={() => { history.goBack() }}>
                                            <i className="fas fa-times" style={{ fontSize: '38px', color: '#bbbbbb' }}></i>
                                        </div>
                                    }
                                </div>
                                <h2 className="line-clamp-2 ml-2 mt-1 mb-0">{service_request.description}</h2>
                                { service_request?.offsite && <span className="badge badge-primary ml-2">Asset is OFFSITE</span> }
                            </div>
                        </div>

                        {/* EXPANDABLE DETAILS ------------------------------------------------------ */}

                        <div className="col-12">
                            <div id="details" className="mb-3">
                                <div className="d-flex justify-content-between">
                                    <div className="d-flex flex-wrap align-items-center position-relative">
                                        { !!expanded &&
                                            <button type="button" 
                                                className={'btn btn-default btn-sm mr-2' }
                                                disabled={!canEditSR}
                                                onClick={openForm}><i className="fa-regular fa-gear fa-lg text-muted mr-1"></i>{ `Edit Service` }
                                            </button>
                                        }
                                        <StatusSelect className="mr-2"
                                            selectedStatusId={service_request.statusId}
                                            statusTypes={Object.values(REQUESTS)}
                                            statusTable={'REQUESTS'}
                                            static={true}
                                        />
                                        <StatusSelect className="mr-2"
                                            selectedStatusId={service_request.serviceRequestTermsId}
                                            statusTypes={Object.values(SERVICE_REQUEST_TERMS)}
                                            statusTable={'SERVICE_REQUEST_TERMS'}
                                            static={true}
                                        />
                                    </div>
                                    { !!expanded &&
                                        <EllipsisDropdown actions={[
                                            ...(user_permissions[PERMISSIONS.ACTIVITY_LOGS_VIEW.key]) ? [{ label: 'Activity Log', onClick: () => setShowActivityModal(true) }] : [],
                                        ]} />
                                    }
                                </div>
                                <ExpandDetails default={(segmentsClosed) ? 'closed' : 'open'} top={ -70 } noControls={ !segmentsClosed } noEllipsis callback={ setExpanded } ref={expandRef}>
                                    <div id="service-request-details" className="mt-3 mb-2">
                                        <DashboardDetail field={ 'Summary' } value={ service_request.description } />
                                        <DashboardDetail field={ 'Suggested' } value={ service_request.suggested } type="text" />
                                        <DashboardDetail field={ 'Total Cost' } value={ service_request.totalCost } type="usd" suffix=" + tax" />
                                        <DashboardDetail field={ 'Created By' } value={ createdBy } type="text" />
                                    </div>
                                </ExpandDetails>

                                { workOrders?.work_orders?.map((work_order) => {
                                    if (!work_order.reason || work_order.statusId != REQUESTS.INCOMPLETE.id) return null;
                                        return (
                                            <div className="alert alert-warning mt-3">
                                                <strong>WO: #{work_order.customId} INCOMPLETE:</strong>&nbsp; { work_order.reason }
                                            </div>
                                        )
                                    })
                                }

                                { service_request.deleted &&
                                    <div className="alert alert-danger mt-3 mb-0 d-flex align-items-center">
                                        <i className="fa-solid fa-circle-xmark fa-2x mr-3"></i>
                                        <span>
                                            This Service Request has been <strong>ARCHIVED!</strong> &nbsp; You may be able to restore it from the <strong>Archive</strong> page on the main menu
                                        </span>
                                    </div>
                                }
                                { features['generate_invoices'] && service_request.statusId == REQUESTS.COMPLETE.id && service_request.type != 'asset_record' &&
                                    <div className="alert alert-info mt-3 mb-0 d-flex align-items-center">
                                        <i className="fa-solid fa-circle-info fa-2x mr-3"></i>
                                        <span>
                                            This Service Request has been Completed!  The next step is to generate an <strong>Invoice</strong> from the <strong>Actions</strong> button.
                                        </span>
                                    </div>
                                }

                                { canEditSR && !admin.bottom_sheet_show && params.form == 'form' && segments <= 10 &&
                                    <SpeedDialButton 
                                        columnRef={ props.columnRef }
                                        onClick={handleSpeedDial}
                                        actions={[
                                            ...(features['manage_payments'] && canReceivePayment) ? [{
                                                name: 'Receive Payment', 
                                                value: 'payment', 
                                                className: '', 
                                                icon: <i className="fa-regular fa-credit-card fa-xl"></i> 
                                            }] : [],
                                            ...(features['generate_invoices'] && canInvoice) ? [{
                                                name: 'Generate Invoice', 
                                                value: 'invoice', 
                                                className: '', 
                                                icon: <i className="fas fa-dollar-sign fa-xl"></i> 
                                            }] : [],
                                            ...(user_permissions[PERMISSIONS.WORK_ORDERS_CREATE.key]) ? [{
                                                name: '+ Work Order', 
                                                value: 'work-order', 
                                                className: '', 
                                                icon: <i className="fa-solid fa-file-plus fa-xl"></i> 
                                            }] : [],
                                            ...(features['generate_quotes'] && user_permissions[PERMISSIONS.QUOTES_CREATE.key]) ? [{
                                                name: '+ Quote', 
                                                value: 'quote', 
                                                className: '', 
                                                icon: <i className="fa-solid fa-comment-plus fa-xl"></i>
                                            }] : [],
                                        ]}
                                    />
                                }
                            </div>
                        </div>

                        {showActivityModal && (
                            <ModalForm
                                modal_header="Service Request Activity"
                                cancel_button_title="Close"
                                cancel_button_callback={() => setShowActivityModal(false)}
                                hide_submit={ true }
                                no_padding={ true }
                                size="md"
                            >
                                <ActivityLog
                                    id="request_id" 
                                    prefix="SERVICE_REQUEST"
                                    singular="service_request"
                                    type={`${LOG.SERVICE_REQUEST.key}`}
                                    store="serviceRequests"
                                />
                            </ModalForm>
                        )}
                    </Authorize>
                : <Spinner center />
            }
        </div>
    );
};

export default ServiceRequestDashboard;
